import { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
    LogOut,
    ChefHat,
    Bell,
    Bike,
    ShoppingBag,
    Phone,
    MapPin,
    RefreshCcw,
    CheckCircle2,
    Clock,
    Image as ImageIcon,
    Volume2,
    VolumeX,
    Trash2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import api, { fmtMVR, API } from "@/lib/api";
import { toast } from "sonner";

const STATUS = {
    pending: {
        label: "Pending",
        color: "bg-pz-warning/15 text-pz-warning ring-pz-warning/30",
    },
    confirmed: {
        label: "Confirmed",
        color: "bg-blue-500/10 text-blue-600 ring-blue-500/30",
    },
    ready: {
        label: "Ready",
        color: "bg-pz-success/15 text-pz-success ring-pz-success/30",
    },
    out_for_delivery: {
        label: "Out for Delivery",
        color: "bg-orange-500/10 text-orange-600 ring-orange-500/30",
    },
    delivery_arrived: {
        label: "Delivery Arrived",
        color: "bg-purple-500/10 text-purple-600 ring-purple-500/30",
    },
    completed: {
        label: "Completed",
        color: "bg-pz-textMuted/15 text-pz-textMuted ring-pz-textMuted/30",
    },
};

const PAYMENT_LABEL = {
    cod: "Cash on Delivery",
    pickup: "Pay on Pickup",
    bank_transfer: "BML Bank Transfer",
};

const ORDER_TYPE_LABEL = {
    delivery: "Delivery",
    takeaway: "Takeaway-Pickup",
    pickup: "Takeaway-Pickup",
    dine_in: "Dine-in",
    dinein: "Dine-in",
};

const NOTIF_AUDIO =
    "data:audio/wav;base64,UklGRrIDAABXQVZFZm10IBAAAAABAAEAESsAACJWAAACABAAZGF0YY4DAAB+f4CAf3+AgIB/foCAgIB/f4CAgH9/gICAf3+AgICAf3+AgIB/f4CAgIB/f4CAgH9/gICAgH9/gICAf3+AgICAf3+AgIB/f4CAgICAf4CAgIB/f4CAgIB/f4CAgH9/gICAgH9/gICAgH+AgICAgH9/gICAgH9/gICAf3+AgICAf3+AgICAf3+AgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/f4CAgIB/gICAgIB/gICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgIB/gICAgIB/gICAgIB/gICAgIB/gICAgIB/gICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+AgICAgH+A";

export default function AdminDashboard() {
    const navigate = useNavigate();
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [selected, setSelected] = useState(null);
    const [soundOn, setSoundOn] = useState(true);
    const [clearingOrders, setClearingOrders] = useState(false);
    const lastSeenIds = useRef(new Set());
    const initialized = useRef(false);

    useEffect(() => {
        if (sessionStorage.getItem("pizano_admin") !== "1") {
            navigate("/admin");
        }
    }, [navigate]);

    const fetchOrders = async () => {
        try {
            const res = await api.get("/orders");
            const fresh = res.data.orders || [];

            if (initialized.current) {
                const newIds = fresh
                    .filter((o) => !lastSeenIds.current.has(o.id))
                    .map((o) => o);

                if (newIds.length > 0) {
                    toast.success(
                        `New order received: #${newIds[0].order_number}`,
                        {
                            description: `${newIds[0].customer_name} • ${fmtMVR(
                                newIds[0].total,
                            )}`,
                        },
                    );

                    if (soundOn) {
                        try {
                            const a = new Audio(NOTIF_AUDIO);
                            a.play().catch(() => {});
                        } catch (e) {}
                    }
                }
            }

            lastSeenIds.current = new Set(fresh.map((o) => o.id));
            initialized.current = true;
            setOrders(fresh);
        } catch (e) {
            toast.error("Failed to load orders");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders();
        const t = setInterval(fetchOrders, 8000);

        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [soundOn]);

    const handleStatus = async (order, next) => {
        try {
            const res = await api.patch(`/orders/${order.id}/status`, {
                status: next,
            });

            setOrders((prev) =>
                prev.map((o) => (o.id === order.id ? res.data : o)),
            );

            if (selected?.id === order.id) {
                setSelected(res.data);
            }

            const nextLabel = STATUS[next]?.label || next;
            toast.success(`Order #${order.order_number} → ${nextLabel}`);
        } catch (err) {
            toast.error("Failed to update status");
        }
    };

    const handleClearAllOrders = async () => {
        if (orders.length === 0) {
            toast.info("There are no orders to clear.");
            return;
        }

        const confirmed = window.confirm(
            "Are you sure you want to permanently delete all orders? This cannot be undone.",
        );

        if (!confirmed) return;

        try {
            setClearingOrders(true);

            await api.delete("/orders/clear-all", {
                headers: {
                    "x-admin-token": "admin-session",
                },
            });

            setOrders([]);
            setSelected(null);
            setFilter("all");
            lastSeenIds.current = new Set();
            initialized.current = true;

            toast.success("All orders cleared.");
        } catch (err) {
            toast.error("Could not clear orders. Try again.");
        } finally {
            setClearingOrders(false);
        }
    };

    const logout = () => {
        sessionStorage.removeItem("pizano_admin");
        navigate("/admin");
    };

    const filtered =
        filter === "all" ? orders : orders.filter((o) => o.status === filter);

    const counts = {
        all: orders.length,
        pending: orders.filter((o) => o.status === "pending").length,
        confirmed: orders.filter((o) => o.status === "confirmed").length,
        ready: orders.filter((o) => o.status === "ready").length,
        out_for_delivery: orders.filter((o) => o.status === "out_for_delivery")
            .length,
        delivery_arrived: orders.filter((o) => o.status === "delivery_arrived")
            .length,
        completed: orders.filter((o) => o.status === "completed").length,
    };

    return (
        <div
            className="min-h-screen bg-pz-bg paper-grain"
            data-testid="admin-dashboard"
        >
            <header className="sticky top-0 z-30 bg-white border-b border-pz-border">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-pz-primary text-white grid place-items-center">
                            <ChefHat size={16} />
                        </div>
                        <span className="font-display text-xl tracking-wide text-pz-text">
                            PIZANO Admin
                        </span>
                    </Link>

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSoundOn((s) => !s)}
                            data-testid="admin-sound-toggle"
                            className="w-9 h-9 rounded-full bg-pz-bg2 grid place-items-center text-pz-text hover:bg-pz-primary hover:text-white transition-colors"
                            title={soundOn ? "Sound on" : "Sound off"}
                        >
                            {soundOn ? (
                                <Volume2 size={16} />
                            ) : (
                                <VolumeX size={16} />
                            )}
                        </button>

                        <Button
                            onClick={handleClearAllOrders}
                            variant="outline"
                            size="sm"
                            disabled={clearingOrders || orders.length === 0}
                            data-testid="admin-clear-orders-btn"
                            className="rounded-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <Trash2 size={14} className="mr-1" />
                            {clearingOrders ? "Clearing..." : "Clear Orders"}
                        </Button>

                        <Button
                            onClick={fetchOrders}
                            variant="outline"
                            size="sm"
                            data-testid="admin-refresh-btn"
                            className="rounded-full border-pz-border text-pz-text hover:bg-pz-bg2"
                        >
                            <RefreshCcw size={14} className="mr-1" /> Refresh
                        </Button>

                        <Button
                            onClick={logout}
                            variant="outline"
                            size="sm"
                            data-testid="admin-logout-btn"
                            className="rounded-full border-pz-border text-pz-text hover:bg-pz-bg2"
                        >
                            <LogOut size={14} className="mr-1" /> Logout
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
                <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
                    <div>
                        <p className="uppercase text-xs tracking-[0.4em] text-pz-primary font-semibold">
                            — Live Orders —
                        </p>
                        <h1 className="font-display text-4xl text-pz-text">
                            Today's Kitchen
                        </h1>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        {[
                            { id: "all", label: "All" },
                            { id: "pending", label: "Pending" },
                            { id: "confirmed", label: "Confirmed" },
                            { id: "ready", label: "Ready" },
                            {
                                id: "out_for_delivery",
                                label: "Out for Delivery",
                            },
                            {
                                id: "delivery_arrived",
                                label: "Delivery Arrived",
                            },
                            { id: "completed", label: "Completed" },
                        ].map((f) => (
                            <button
                                key={f.id}
                                onClick={() => setFilter(f.id)}
                                data-testid={`filter-${f.id}`}
                                className={`px-4 py-2 rounded-full text-xs uppercase tracking-wider font-bold transition-all ${
                                    filter === f.id
                                        ? "bg-pz-text text-white"
                                        : "bg-white text-pz-text border border-pz-border hover:border-pz-primary"
                                }`}
                            >
                                {f.label}{" "}
                                <span className="opacity-60 ml-1">
                                    {counts[f.id]}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="bg-white border border-pz-border rounded-2xl h-64 animate-pulse"
                            />
                        ))}
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="bg-white border border-pz-border rounded-2xl p-12 text-center">
                        <Bell
                            size={32}
                            className="mx-auto text-pz-textMuted mb-3"
                        />
                        <p className="text-pz-textMuted">
                            No orders to show yet. New orders will appear here
                            automatically.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {filtered.map((o) => (
                            <OrderCard
                                key={o.id}
                                order={o}
                                onStatus={handleStatus}
                                onView={() => setSelected(o)}
                            />
                        ))}
                    </div>
                )}
            </main>

            <Dialog
                open={!!selected}
                onOpenChange={(o) => !o && setSelected(null)}
            >
                <DialogContent className="bg-white rounded-2xl max-w-lg p-0 overflow-hidden border-pz-border">
                    {selected && (
                        <>
                            <DialogHeader className="bg-pz-text text-white p-5 text-left">
                                <DialogTitle className="font-display text-3xl text-white">
                                    #{selected.order_number}
                                </DialogTitle>
                                <p className="text-white/70 text-sm">
                                    {new Date(
                                        selected.created_at,
                                    ).toLocaleString("en-GB", {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                    })}
                                </p>
                            </DialogHeader>

                            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
                                <DetailRow
                                    icon={Phone}
                                    label="Phone"
                                    value={selected.phone}
                                />

                                {selected.address && (
                                    <DetailRow
                                        icon={MapPin}
                                        label="Address"
                                        value={selected.address}
                                    />
                                )}

                                <DetailRow
                                    icon={
                                        selected.order_type === "delivery"
                                            ? Bike
                                            : ShoppingBag
                                    }
                                    label="Type"
                                    value={
                                        ORDER_TYPE_LABEL[
                                            selected.order_type
                                        ] || selected.order_type
                                    }
                                />

                                <DetailRow
                                    icon={Clock}
                                    label="Payment"
                                    value={
                                        PAYMENT_LABEL[
                                            selected.payment_method
                                        ] || selected.payment_method
                                    }
                                />

                                {selected.payment_screenshot && (
                                    <div>
                                        <p className="text-xs uppercase font-semibold text-pz-textMuted mb-2">
                                            Payment Screenshot
                                        </p>
                                        <a
                                            href={`${API}/uploads/${selected.payment_screenshot}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            data-testid="view-screenshot-link"
                                        >
                                            <img
                                                src={`${API}/uploads/${selected.payment_screenshot}`}
                                                alt="Payment screenshot"
                                                className="w-full max-h-64 object-contain rounded-xl border border-pz-border bg-pz-bg2"
                                            />
                                        </a>
                                    </div>
                                )}

                                {selected.notes && (
                                    <div className="bg-pz-bg2 rounded-xl p-3">
                                        <p className="text-xs uppercase font-semibold text-pz-textMuted mb-1">
                                            Notes
                                        </p>
                                        <p className="text-sm text-pz-text">
                                            {selected.notes}
                                        </p>
                                    </div>
                                )}

                                <div className="border-t border-pz-border pt-4">
                                    <p className="text-xs uppercase font-semibold text-pz-textMuted mb-2">
                                        Items
                                    </p>

                                    <div className="space-y-1.5">
                                        {selected.items?.map((i) => (
                                            <div
                                                key={i.id}
                                                className="flex justify-between text-sm"
                                            >
                                                <span className="text-pz-text">
                                                    <b>{i.quantity}×</b>{" "}
                                                    {i.name}
                                                </span>
                                                <span className="text-pz-textMuted">
                                                    {fmtMVR(
                                                        i.price * i.quantity,
                                                    )}
                                                </span>
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex justify-between mt-3 pt-3 border-t border-pz-border">
                                        <span className="font-semibold text-pz-text uppercase tracking-wider text-xs">
                                            Total
                                        </span>
                                        <span className="font-display text-2xl text-pz-primary">
                                            {fmtMVR(selected.total)}
                                        </span>
                                    </div>
                                </div>

                                <StatusActions
                                    order={selected}
                                    onStatus={handleStatus}
                                />
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function DetailRow({ icon: Icon, label, value }) {
    return (
        <div className="flex items-start gap-3">
            <Icon size={16} className="text-pz-primary mt-0.5" />
            <div>
                <p className="text-xs uppercase font-semibold text-pz-textMuted">
                    {label}
                </p>
                <p className="text-sm text-pz-text capitalize">{value}</p>
            </div>
        </div>
    );
}

function OrderCard({ order, onStatus, onView }) {
    const s = STATUS[order.status] || STATUS.pending;

    return (
        <div
            data-testid={`order-card-${order.order_number}`}
            className="bg-white border border-pz-border rounded-2xl p-5 hover:shadow-lg transition-shadow flex flex-col"
        >
            <div className="flex items-start justify-between mb-3">
                <div>
                    <p className="text-xs uppercase tracking-widest text-pz-textMuted">
                        Order
                    </p>
                    <div className="font-display text-3xl text-pz-text">
                        #{order.order_number}
                    </div>
                </div>

                <span
                    className={`inline-flex items-center px-3 py-1 rounded-full ring-1 text-[10px] uppercase tracking-wider font-bold ${s.color}`}
                >
                    {s.label}
                </span>
            </div>

            <div className="space-y-1.5 text-sm flex-1">
                <p className="text-pz-text font-semibold">
                    {order.customer_name}
                </p>

                <p className="text-pz-textMuted text-xs">{order.phone}</p>

                <div className="flex items-center gap-2 text-pz-textMuted text-xs">
                    {order.order_type === "delivery" ? (
                        <Bike size={12} />
                    ) : (
                        <ShoppingBag size={12} />
                    )}
                    <span className="capitalize">
                        {ORDER_TYPE_LABEL[order.order_type] ||
                            order.order_type}
                    </span>
                    <span>•</span>
                    <span>
                        {PAYMENT_LABEL[order.payment_method] ||
                            order.payment_method}
                    </span>
                </div>

                <div className="text-xs text-pz-textMuted">
                    {order.items?.length} item
                    {order.items?.length !== 1 ? "s" : ""}
                </div>
            </div>

            <div className="flex items-center justify-between pt-4 mt-3 border-t border-pz-border">
                <span className="font-display text-2xl text-pz-primary">
                    {fmtMVR(order.total)}
                </span>

                <div className="flex gap-2">
                    {order.payment_screenshot && (
                        <button
                            onClick={onView}
                            data-testid={`view-${order.order_number}`}
                            className="text-pz-textMuted hover:text-pz-primary p-2"
                            title="View screenshot"
                        >
                            <ImageIcon size={16} />
                        </button>
                    )}

                    <Button
                        onClick={onView}
                        size="sm"
                        variant="outline"
                        data-testid={`details-${order.order_number}`}
                        className="rounded-full border-pz-border text-pz-text hover:bg-pz-bg2"
                    >
                        Details
                    </Button>
                </div>
            </div>

            <div className="mt-2">
                <StatusActions order={order} onStatus={onStatus} compact />
            </div>
        </div>
    );
}

function StatusActions({ order, onStatus, compact }) {
    const isDelivery = order.order_type === "delivery";

    const deliveryFlow = {
        pending: { id: "confirmed", label: "Confirm" },
        confirmed: { id: "out_for_delivery", label: "Out for Delivery" },
        out_for_delivery: {
            id: "delivery_arrived",
            label: "Delivery Arrived",
        },
        delivery_arrived: {
            id: "completed",
            label: "Confirm Delivery",
        },
        completed: null,
    };

    const normalFlow = {
        pending: { id: "confirmed", label: "Confirm" },
        confirmed: { id: "ready", label: "Mark Ready" },
        ready: { id: "completed", label: "Complete" },
        completed: null,
    };

    const next = isDelivery
        ? deliveryFlow[order.status]
        : normalFlow[order.status];

    if (!next) return null;

    return (
        <Button
            onClick={() => onStatus(order, next.id)}
            data-testid={`status-${order.order_number}-${next.id}`}
            size={compact ? "sm" : "default"}
            className="w-full mt-1 rounded-full bg-pz-primary hover:bg-pz-primaryHover text-white uppercase tracking-wider text-xs font-bold"
        >
            <CheckCircle2 size={14} className="mr-1" />
            {next.label}
        </Button>
    );
}
